import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Send, 
  MessageSquare, 
  Mail,
  Users,
  Megaphone
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/toast";
import api from "@/lib/marketing-api";

export default function BroadcastPage() {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // Channel broadcast
  const [channelData, setChannelData] = useState({
    channel_id: "",
    message: ""
  });

  // SMS broadcast  
  const [smsData, setSmsData] = useState({
    phone_number: "",
    message: ""
  });

  const sendToChannel = async (e) => {
    e.preventDefault();
    if (!channelData.channel_id || !channelData.message) {
      toast.error(t('errors.fillAllFields'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/telegram/channel/send', channelData);
      toast.success(t('success.sent'));
      setChannelData({ channel_id: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const sendSMS = async (e) => {
    e.preventDefault();
    if (!smsData.phone_number || !smsData.message) {
      toast.error(t('errors.fillAllFields'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/sms/send', smsData);
      toast.success(t('success.sent'));
      setSmsData({ phone_number: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="broadcast-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-foreground">{t('marketingFull.broadcastTitle')}</h1>
        <p className="text-muted-foreground mt-1">{t('marketingFull.broadcastSubtitle')}</p>
      </div>

      <Tabs defaultValue="channel" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="channel" className="flex items-center gap-2">
            <Megaphone className="w-4 h-4" />
            {t('marketingFull.telegramChannel')}
          </TabsTrigger>
          <TabsTrigger value="sms" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            {t('marketingFull.smsMessage')}
          </TabsTrigger>
        </TabsList>

        {/* Telegram Channel */}
        <TabsContent value="channel">
          <Card data-testid="channel-broadcast">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-blue-400" />
                {t('marketingFull.sendToChannel')}
              </CardTitle>
              <CardDescription>
                {t('marketingFull.sendToChannelDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={sendToChannel} className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="channel_id">{t('marketingFull.channelId')}</Label>
                  <Input
                    id="channel_id"
                    value={channelData.channel_id}
                    onChange={(e) => setChannelData({ ...channelData, channel_id: e.target.value })}
                    placeholder={t('marketingFull.channelIdPlaceholder')}
                    data-testid="channel-id-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('marketingFull.channelIdNote')}
                  </p>
                </div>
                <div className="form-group">
                  <Label htmlFor="channel_message">{t('marketingFull.message')}</Label>
                  <Textarea
                    id="channel_message"
                    value={channelData.message}
                    onChange={(e) => setChannelData({ ...channelData, message: e.target.value })}
                    placeholder={t('marketingFull.messageHtmlPlaceholder')}
                    rows={6}
                    data-testid="channel-message-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="send-channel-btn">
                  {loading ? (
                    <div className="spinner me-2" />
                  ) : (
                    <Send className="w-4 h-4 me-2" />
                  )}
                  {t('marketingFull.sendToChannelBtn')}
                </Button>
              </form>

              <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <h4 className="font-medium mb-2 text-blue-400">{t('marketingFull.howToSetup')}</h4>
                <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                  <li>{isRTL ? "أنشئ قناة تلجرام جديدة أو استخدم قناة موجودة" : "Create a new Telegram channel or use existing one"}</li>
                  <li>{isRTL ? "اذهب إلى إعدادات القناة → المسؤولين" : "Go to channel settings → Administrators"}</li>
                  <li>{isRTL ? "أضف البوت كمسؤول مع صلاحية إرسال الرسائل" : "Add bot as admin with message sending permission"}</li>
                  <li>{isRTL ? "استخدم @اسم_القناة أو انسخ رابط القناة" : "Use @channel_name or copy channel link"}</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS */}
        <TabsContent value="sms">
          <Card data-testid="sms-broadcast">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-400" />
                {t('marketingFull.sendSmsMessage')}
              </CardTitle>
              <CardDescription>
                {t('marketingFull.sendSmsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={sendSMS} className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="phone_number">{t('marketingFull.phoneNumber')}</Label>
                  <Input
                    id="phone_number"
                    value={smsData.phone_number}
                    onChange={(e) => setSmsData({ ...smsData, phone_number: e.target.value })}
                    placeholder="+1234567890"
                    className="font-numbers"
                    data-testid="sms-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('marketingFull.phoneNumberNote')}
                  </p>
                </div>
                <div className="form-group">
                  <Label htmlFor="sms_message">{t('marketingFull.message')}</Label>
                  <Textarea
                    id="sms_message"
                    value={smsData.message}
                    onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
                    placeholder={t('marketingFull.smsMessagePlaceholder')}
                    rows={4}
                    maxLength={160}
                    data-testid="sms-message-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {smsData.message.length}/160 {t('marketingFull.characters')}
                  </p>
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="send-sms-btn">
                  {loading ? (
                    <div className="spinner me-2" />
                  ) : (
                    <MessageSquare className="w-4 h-4 me-2" />
                  )}
                  {t('marketingFull.sendSms')}
                </Button>
              </form>

              <div className="mt-6 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <h4 className="font-medium mb-2 text-green-400">{t('marketingFull.importantNotes')}</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• {isRTL ? "يجب إعداد Twilio في صفحة الإعدادات أولاً" : "Twilio must be configured in settings first"}</li>
                  <li>• {isRTL ? "كل رسالة SMS لها تكلفة حسب الدولة" : "Each SMS has a cost based on country"}</li>
                  <li>• {isRTL ? "الحد الأقصى 160 حرف للرسالة الواحدة" : "Maximum 160 characters per message"}</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Stats */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-lg">{t('marketingFull.channelComparison')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-4 rounded-lg bg-blue-500/10">
              <h5 className="font-bold text-blue-400 mb-2">{t('marketingFull.telegramChannel')}</h5>
              <ul className="text-muted-foreground space-y-1">
                <li>✅ {t('marketingFull.completelyFree')}</li>
                <li>✅ {t('marketingFull.noSubscriberLimit')}</li>
                <li>✅ {t('marketingFull.instantDelivery')}</li>
                <li>✅ {t('marketingFull.supportsMultimedia')}</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-green-500/10">
              <h5 className="font-bold text-green-400 mb-2">SMS</h5>
              <ul className="text-muted-foreground space-y-1">
                <li>✅ {t('marketingFull.reachesEveryone')}</li>
                <li>✅ {t('marketingFull.highOpenRate')}</li>
                <li>⚠️ {t('marketingFull.costPerMessage')}</li>
                <li>⚠️ {t('marketingFull.onlyCharacters')}</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-purple-500/10">
              <h5 className="font-bold text-purple-400 mb-2">{t('marketingFull.email')}</h5>
              <ul className="text-muted-foreground space-y-1">
                <li>✅ {t('marketingFull.richContent')}</li>
                <li>✅ {t('marketingFull.openClickTracking')}</li>
                <li>⚠️ {t('marketingFull.mediumOpenRate')}</li>
                <li>⚠️ {t('marketingFull.mayGoToSpam')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
